import { LatencyModeChangedEvent, PlayerAPI, PlayerEvent, TargetLatencyChangedEvent } from 'bitmovin-player';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { isPlayerAlive } from '../../../helpers/playerGuards';

/**
 * Debug-only tooling for stream playback. Not part of the product UI: the panel and
 * the console log stay off until the unlock code is typed (typing it again turns them
 * off), and everything here can be deleted in one go once low latency tuning is done.
 *
 * The panel opens on any stream. The low latency readouts (mode chips, target
 * latency slider) only make sense on a low latency source, so they are rendered
 * only when `isLowLatency` is set â€” the rest (time, bitrate, measured latency
 * where the player reports one) works on regular streams too.
 */

const UNLOCK_CODE = '4275';
const STATS_INTERVAL_MS = 200;
/** The panel refreshes every 200ms; the console gets one line per second so it stays readable. */
const CONSOLE_LOG_INTERVAL_MS = 1000;
const EVENT_BINDING_DELAY_MS = 500;

/** Highlight for the active latency mode, shared by the panel chips and the console log. */
const ACTIVE_MODE_COLORS: Record<string, { background: string; text: string }> = {
    idle: { background: 'green', text: 'white' },
    catchup: { background: 'yellow', text: 'black' },
    fallback: { background: 'yellow', text: 'black' },
    suspended: { background: 'gray', text: 'white' },
};

type LowLatencyDebug = {
    isLowLatency: boolean;
    showPanel: boolean;
    hidePanel: () => void;
    targetLatency: number;
    handleTargetLatencyChange: (event: { target: { value: string } }) => void;
    /** Call from the player's `onReady` to start reporting latency/bitrate into the panel and console. */
    attachToPlayer: (player: PlayerAPI) => void;
};

/** The lowlatency API throws (or reports NaN) on sources it does not apply to. */
const readLatency = (player: PlayerAPI): number | null => {
    try {
        const latency = player.lowlatency.getLatency();
        return Number.isFinite(latency) ? latency : null;
    } catch {
        return null;
    }
};

type DebugState = { isDebugEnabled: boolean; isLowLatency: boolean; targetLatency: number };

/** Last mode reported by `LatencyModeChanged` — the lowlatency API has no getter for it. */
type LatencyModeRef = MutableRefObject<LatencyModeChangedEvent['to'] | null>;

const startStatsPolling = (player: PlayerAPI, getDebugState: () => DebugState, latencyMode: LatencyModeRef) => {
    let lastLoggedAt = 0;

    const interval = setInterval(() => {
        const { isDebugEnabled, isLowLatency, targetLatency } = getDebugState();
        if (!isDebugEnabled) return;
        const timeDisplay: HTMLElement | null = document.querySelector('#time');
        const latencyDisplay: HTMLElement | null = document.querySelector('#latency');
        const bitrateDisplay: HTMLElement | null = document.querySelector('#bitrate');
        if (player && !player.isPaused()) {
            const currentLatency = readLatency(player);
            const currentBitrate = player.getPlaybackVideoData?.()?.bitrate;
            const utcTime = new Date().getUTCHours();
            const estTime = new Date();
            estTime.setHours(utcTime - 4);

            const time = estTime.toISOString().substr(11, 10).substr(3);
            const latency = currentLatency === null ? 'n/a' : `${Math.round(currentLatency * 1000) / 1000}s`;
            const bitrate =
                typeof currentBitrate === 'number' && currentBitrate > 0
                    ? `${Math.round(currentBitrate / 1000)} kbps`
                    : 'n/a';

            if (latencyDisplay) {
                latencyDisplay.innerText = `Latency: ${latency}`;
            }
            if (bitrateDisplay) {
                bitrateDisplay.innerText = `Bitrate: ${bitrate}`;
            }
            if (timeDisplay) {
                timeDisplay.innerText = `Time: ${time}`;
            }

            const now = Date.now();
            if (now - lastLoggedAt >= CONSOLE_LOG_INTERVAL_MS) {
                lastLoggedAt = now;
                const readouts = `Time: ${time} | Latency: ${latency} | Bitrate: ${bitrate}`;
                if (isLowLatency) {
                    const mode = latencyMode.current ?? 'n/a';
                    const modeColors = ACTIVE_MODE_COLORS[mode];
                    const modeStyle = modeColors
                        ? `background: ${modeColors.background}; color: ${modeColors.text}; padding: 0 4px`
                        : '';
                    const target = `${targetLatency.toFixed(1)}s`;
                    console.log(
                        `[LowLatencyDebug] Latency mode: %c${mode}%c | Target latency: ${target} | ${readouts}`,
                        modeStyle,
                        ''
                    );
                } else {
                    console.log(`[LowLatencyDebug] Regular stream | ${readouts}`);
                }
            }
        }
    }, STATS_INTERVAL_MS);

    return () => clearInterval(interval);
};

const bindLatencyEvents = (
    player: PlayerAPI,
    setTargetLatency: (value: number) => void,
    latencyMode: LatencyModeRef,
    isDebugEnabled: () => boolean
) => {
    const onLatencyModeChanged = (event: LatencyModeChangedEvent) => {
        latencyMode.current = event.to;
        const latencyModeDisplay = {
            idle: document.querySelector('#idle') as HTMLElement | null,
            catchup: document.querySelector('#catchup') as HTMLElement | null,
            fallback: document.querySelector('#fallback') as HTMLElement | null,
            suspended: document.querySelector('#suspended') as HTMLElement | null,
        };
        if (isDebugEnabled()) {
            console.log('View mode changed: ', event.timestamp, event.type, event.to);
        }
        Object.keys(latencyModeDisplay).forEach((key) => {
            const node = latencyModeDisplay[key as keyof typeof latencyModeDisplay];
            if (node) {
                const isActive = key === event.to;
                node.style.backgroundColor = isActive ? ACTIVE_MODE_COLORS[key].background : 'gray';
                node.style.color = isActive ? ACTIVE_MODE_COLORS[key].text : '';
            }
        });
    };

    const onTargetLatencyChanged = (event: TargetLatencyChangedEvent) => {
        setTargetLatency(event.to);
        if (!isDebugEnabled()) return;

        console.log('Target latency changed: ', event);
        setTimeout(() => {
            console.log('Current latency after target latency change: ', player?.lowlatency.getLatency());
        }, 2000);
    };

    const timer = setTimeout(() => {
        if (player && player.on) {
            player.on(PlayerEvent.LatencyModeChanged, onLatencyModeChanged);
            player.on(PlayerEvent.TargetLatencyChanged, onTargetLatencyChanged);
        }
    }, EVENT_BINDING_DELAY_MS);

    return () => {
        clearTimeout(timer);
        if (player && player.off && isPlayerAlive(player)) {
            player.off(PlayerEvent.LatencyModeChanged, onLatencyModeChanged);
            player.off(PlayerEvent.TargetLatencyChanged, onTargetLatencyChanged);
        }
    };
};

export const useLowLatencyDebug = (
    isLowLatency: boolean,
    playerInstanceRef: MutableRefObject<PlayerAPI | null>
): LowLatencyDebug => {
    const [isDebugEnabled, setIsDebugEnabled] = useState(false);
    const [targetLatency, setTargetLatency] = useState(3);
    const detachRef = useRef<(() => void) | null>(null);

    /**
     * `attachToPlayer` is baked into the player config when the player is created, which
     * happens before debug is ever turned on — so the polling loop would capture a stale
     * `isDebugEnabled` (and target latency). Read them through a ref instead of capturing them at attach time.
     */
    const debugStateRef = useRef<DebugState>({ isDebugEnabled, isLowLatency, targetLatency });
    debugStateRef.current = { isDebugEnabled, isLowLatency, targetLatency };

    // Toggle the panel and console log by typing the code during playback, on any stream.
    useEffect(() => {
        let typedDigits = '';

        const onKeyDown = (event: KeyboardEvent) => {
            if (!/^\d$/.test(event.key)) {
                typedDigits = '';
                return;
            }

            typedDigits = (typedDigits + event.key).slice(-UNLOCK_CODE.length);
            if (typedDigits === UNLOCK_CODE) {
                const nextIsDebugEnabled = !debugStateRef.current.isDebugEnabled;
                console.log(`[LowLatencyDebug] ${nextIsDebugEnabled ? 'enabled' : 'disabled'}`);
                setIsDebugEnabled(nextIsDebugEnabled);
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => () => detachRef.current?.(), []);

    const attachToPlayer = (player: PlayerAPI) => {
        if (!player) return;

        detachRef.current?.();

        try {
            const initialTargetLatency = player.lowlatency.getTargetLatency?.();
            if (typeof initialTargetLatency === 'number' && initialTargetLatency > 0) {
                setTargetLatency(initialTargetLatency);
            }
        } catch {
            // not a low latency source â€” keep the default target latency
        }

        const latencyMode: LatencyModeRef = { current: null };
        const stopPolling = startStatsPolling(player, () => debugStateRef.current, latencyMode);
        const unbindEvents = bindLatencyEvents(
            player,
            setTargetLatency,
            latencyMode,
            () => debugStateRef.current.isDebugEnabled
        );

        detachRef.current = () => {
            stopPolling();
            unbindEvents();
            detachRef.current = null;
        };
    };

    const handleTargetLatencyChange = (event: { target: { value: string } }) => {
        const nextTargetLatency = Number(event.target.value);
        setTargetLatency(nextTargetLatency);

        const player = playerInstanceRef.current;
        if (!player || !isPlayerAlive(player)) return;

        try {
            player.lowlatency.setTargetLatency(nextTargetLatency);
        } catch (e) {
            console.warn('Target latency is not settable on this source:', e);
        }
    };

    return {
        isLowLatency,
        showPanel: isDebugEnabled,
        hidePanel: () => setIsDebugEnabled(false),
        targetLatency,
        handleTargetLatencyChange,
        attachToPlayer,
    };
};

const modeChipStyle = { backgroundColor: 'gray', marginInline: '5px', padding: '2px 5px' };
const readoutStyle = { backgroundColor: 'rgba(0, 0, 0, 0.5)' };

type LowLatencyDebugPanelProps = Pick<
    LowLatencyDebug,
    'isLowLatency' | 'showPanel' | 'hidePanel' | 'targetLatency' | 'handleTargetLatencyChange'
>;

export const LowLatencyDebugPanel = ({
    isLowLatency,
    showPanel,
    hidePanel,
    targetLatency,
    handleTargetLatencyChange,
}: LowLatencyDebugPanelProps) => (
    <div
        style={{
            position: 'absolute',
            zIndex: 9999,
            color: 'white',
            padding: '5px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            visibility: showPanel ? 'visible' : 'hidden',
            top: '0',
            insetInlineEnd: '0px',
            fontSize: '20px',
            border: '1px solid #f86060',
        }}
    >
        <button
            id="latency-panel-toggle"
            type="button"
            onClick={hidePanel}
            style={{
                marginBlockEnd: '8px',
                backgroundColor: '#222',
                color: 'white',
                border: '1px solid #666',
                padding: '2px 8px',
                cursor: 'pointer',
            }}
        >
            Hide panel
        </button>
        {isLowLatency ? (
            <>
                <div>Latency mode:</div>
                <span id="idle" style={modeChipStyle}>
                    Idle
                </span>
                <span id="catchup" style={modeChipStyle}>
                    Catch-up
                </span>
                <span id="fallback" style={modeChipStyle}>
                    Fallback
                </span>
                <span id="suspended" style={modeChipStyle}>
                    Suspended
                </span>
                <hr />
                <div style={{ marginBlockEnd: '6px' }}>
                    <label htmlFor="target-latency-slider">Target latency: {targetLatency.toFixed(1)}s</label>
                    <input
                        id="target-latency-slider"
                        type="range"
                        min="1"
                        max="10"
                        step="0.1"
                        value={targetLatency}
                        onChange={handleTargetLatencyChange}
                        style={{ width: '100%', marginBlockStart: '4px' }}
                    />
                </div>
            </>
        ) : (
            <div style={{ marginBlockEnd: '6px' }}>Regular stream</div>
        )}
        <div id="time" style={readoutStyle}></div>
        <div id="latency" style={readoutStyle}></div>
        <div id="bitrate" style={readoutStyle}></div>
    </div>
);

export default LowLatencyDebugPanel;
